import React, { useEffect, useMemo, useState } from 'react';
import { Ban, Plus, X, RefreshCw } from 'lucide-react';
import AppShell from '../components/AppShell';
import { Button } from '../components/ui/button';
import axiosInstance from '../utils/axiosInstance';

const BlockedSenders = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newEntry, setNewEntry] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('api/replai/settings/');
      setEntries(Array.isArray(data?.excluded_senders) ? data.excluded_senders : []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const save = async (next) => {
    setSaving(true);
    try {
      await axiosInstance.patch('api/replai/settings/', {
        excluded_senders: next,
      });
      setEntries(next);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => { load(); }, []);

  const addEntry = (e) => {
    e.preventDefault();
    const v = newEntry.trim().toLowerCase().replace(/^@+/, '');
    if (!v) return;
    if (entries.includes(v)) {
      setNewEntry('');
      return;
    }
    save([...entries, v]);
    setNewEntry('');
  };

  const removeEntry = (v) => {
    save(entries.filter((e) => e !== v));
  };

  const grouped = useMemo(() => {
    const domains = entries.filter((e) => !e.includes('@'));
    const emails = entries.filter((e) => e.includes('@'));
    return { domains, emails };
  }, [entries]);

  return (
    <AppShell
      title="Blocked senders"
      subtitle="Domains and full email addresses Replai auto-archives on ingest. Click the X to unblock."
    >
      <section className="bg-white rounded-2xl border border-border shadow-sm p-6 mb-6">
        <form onSubmit={addEntry} className="flex gap-2">
          <input
            type="text"
            value={newEntry}
            onChange={(e) => setNewEntry(e.target.value)}
            placeholder="envia.com  or  spam@example.com"
            className="h-10 flex-1 rounded-xl border border-border bg-white px-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]"
          />
          <Button type="submit" disabled={saving || !newEntry.trim()} className="gap-2">
            <Plus className="h-4 w-4" />
            Block
          </Button>
        </form>
        <p className="mt-2 text-xs text-muted-foreground">
          Enter a domain (blocks every email from that domain) or a full email address (blocks that one sender only).
        </p>
      </section>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Section title="Domains" icon={Ban} entries={grouped.domains} onRemove={removeEntry} saving={saving} loading={loading && entries.length === 0} />
        <Section title="Email addresses" icon={Ban} entries={grouped.emails} onRemove={removeEntry} saving={saving} loading={loading && entries.length === 0} />
      </div>
    </AppShell>
  );
};

const Section = ({ title, icon: Icon, entries, onRemove, saving, loading }) => (
  <section className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden">
    <div className="px-5 py-3 border-b border-border/70 flex items-center justify-between">
      <div className="flex items-center gap-2 text-sm font-medium text-foreground">
        <Icon className="h-4 w-4" />
        {title}
        <span className="text-[0.65rem] text-muted-foreground">({entries.length})</span>
      </div>
      {(loading || saving) && <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />}
    </div>
    <ul className="divide-y divide-border/70 max-h-[60vh] overflow-y-auto">
      {entries.length === 0 && !loading && (
        <li className="px-5 py-8 text-center text-sm text-muted-foreground italic">
          Nothing here yet.
        </li>
      )}
      {entries.map((v) => (
        <li key={v} className="px-5 py-2 flex items-center justify-between gap-3">
          <code className="text-sm text-foreground">{v}</code>
          <button
            type="button"
            onClick={() => onRemove(v)}
            disabled={saving}
            className="rounded-lg p-1 text-muted-foreground hover:bg-rose-50 hover:text-rose-600"
            title="Unblock"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </li>
      ))}
    </ul>
  </section>
);

export default BlockedSenders;
